import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { moroccoRegions, allPartiesIncludingNonParliamentary } from '@/data/parties';

export interface ParseResult<T> {
  data: T[];
  errors: string[];
}

export const parseExcel = <T>(file: File, expectedColumns: string[]): Promise<ParseResult<T>> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        
        // On prend la première feuille de calcul
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convertit en JSON (tableau d'objets)
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" }) as any[];
        
        if (jsonData.length === 0) {
          return resolve({ data: [], errors: ["Le fichier est vide."] });
        }

        // Vérification des colonnes obligatoires dans la première ligne
        const headers = Object.keys(jsonData[0]).map(h => h.trim().toLowerCase());
        const missingColumns = expectedColumns.filter(col => 
          !headers.includes(col.toLowerCase())
        );

        if (missingColumns.length > 0) {
          return resolve({
            data: [],
            errors: [`Colonnes manquantes ou mal nommées : ${missingColumns.join(', ')}`]
          });
        }

        // Transformation et nettoyage
        const cleanedData = jsonData.map(row => {
          const cleanedRow: any = {};
          Object.keys(row).forEach(key => {
            cleanedRow[key.trim()] = String(row[key]).trim();
          });
          return cleanedRow as T;
        });

        resolve({ data: cleanedData, errors: [] });
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = (error) => reject(error);
    
    reader.readAsBinaryString(file);
  });
};

export const generateExcelTemplate = async (columns: string[], filename: string = "Modele_Import.xlsx", type: "candidates" | "propositions" = "candidates") => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Modèle');
  
  // Création d'une feuille cachée pour les listes de validation
  const validationSheet = workbook.addWorksheet('Validations', { state: 'hidden' });
  
  // Remplissage de la feuille cachée avec les régions et provinces
  const regionsList = moroccoRegions.map(r => r.name);
  const allProvinces = moroccoRegions.flatMap(r => r.provinces);
  const partyAcronyms = allPartiesIncludingNonParliamentary.map(p => p.acronym);
  
  validationSheet.getColumn('A').values = ['Regions', ...regionsList];
  validationSheet.getColumn('B').values = ['Provinces', ...allProvinces];
  validationSheet.getColumn('C').values = ['Parties', ...partyAcronyms];

  // Configuration des colonnes
  worksheet.columns = columns.map(col => ({ header: col, key: col, width: 20 }));
  
  // Style de l'en-tête
  worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
  worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } }; // Emerald-600

  // Ajout des validations de données sur les 500 premières lignes
  for (let i = 2; i <= 500; i++) {
    // Parti (for both candidates and propositions)
    const partiColIndex = columns.findIndex(c => c.toLowerCase() === 'parti') + 1;
    if (partiColIndex > 0) {
      worksheet.getCell(i, partiColIndex).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`Validations!$C$2:$C$${partyAcronyms.length + 1}`]
      };
    }

    if (type === "candidates") {
      // Région
      const regionColIndex = columns.findIndex(c => c.toLowerCase() === 'region') + 1;
      if (regionColIndex > 0) {
        worksheet.getCell(i, regionColIndex).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [`Validations!$A$2:$A$${regionsList.length + 1}`]
        };
      }
      
      // Province
      const provinceColIndex = columns.findIndex(c => c.toLowerCase() === 'province') + 1;
      if (provinceColIndex > 0) {
        worksheet.getCell(i, provinceColIndex).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [`Validations!$B$2:$B$${allProvinces.length + 1}`]
        };
      }



      // Type de liste
      const listTypeIndex = columns.findIndex(c => c.toLowerCase() === 'listtype') + 1;
      if (listTypeIndex > 0) {
        worksheet.getCell(i, listTypeIndex).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: ['"locale,regionale"']
        };
      }
      
      // Genre
      const genreIndex = columns.findIndex(c => c.toLowerCase() === 'genre') + 1;
      if (genreIndex > 0) {
        worksheet.getCell(i, genreIndex).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: ['"Homme,Femme"']
        };
      }
      
      // Date de naissance
      const dateIndex = columns.findIndex(c => c.toLowerCase() === 'datenaissance') + 1;
      if (dateIndex > 0) {
        worksheet.getCell(i, dateIndex).dataValidation = {
          type: 'date',
          allowBlank: true,
          operator: 'between',
          showInputMessage: true,
          promptTitle: 'Format de date',
          prompt: 'Saisissez une date (ex: 15/08/1980)',
          formulae: [new Date('1900-01-01'), new Date('2008-01-01')]
        };
        worksheet.getCell(i, dateIndex).numFmt = 'dd/mm/yyyy';
      }
    }
  }

  // Génération du fichier Excel
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), filename);
};
